import React from 'react';
import Link from '@mui/material/Link';

const emptyText = '--';

const DIRECT_LAT_KEYS = ['lat', 'latitude', 'Lat', 'Latitude', 'LAT'];
const DIRECT_LNG_KEYS = ['lng', 'lon', 'long', 'longitude', 'Lng', 'Lon', 'Long', 'Longitude', 'LNG'];
const START_LAT_KEYS = ['startLat', 'start_lat', 'startLatitude', 'start_latitude', 'startLocLat', 'start_loc_lat', 'startLocationLat', 'startAddressLat', 'fromLat', 'from_lat', 'tripStartLat', 'trip_start_lat'];
const START_LNG_KEYS = ['startLng', 'start_lng', 'startLon', 'startLongitude', 'start_longitude', 'startLocLng', 'start_loc_lng', 'startLocationLng', 'startAddressLng', 'fromLng', 'from_lng', 'fromLon', 'tripStartLng', 'trip_start_lng'];
const END_LAT_KEYS = ['endLat', 'end_lat', 'endLatitude', 'end_latitude', 'endLocLat', 'end_loc_lat', 'endLocationLat', 'endAddressLat', 'toLat', 'to_lat', 'tripEndLat', 'trip_end_lat'];
const END_LNG_KEYS = ['endLng', 'end_lng', 'endLon', 'endLongitude', 'end_longitude', 'endLocLng', 'end_loc_lng', 'endLocationLng', 'endAddressLng', 'toLng', 'to_lng', 'toLon', 'tripEndLng', 'trip_end_lng'];

const isPresent = (value) => value !== undefined && value !== null && value !== '';

const parseCoordinate = (value, limit) => {
    if (!isPresent(value)) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || Math.abs(parsed) > limit) return null;
    return parsed;
};

const pickValue = (source, keys) => {
    if (!source || typeof source !== 'object') return undefined;
    return keys.map((key) => source[key]).find(isPresent);
};

const pickPair = (source, latKeys, lngKeys) => {
    const lat = parseCoordinate(pickValue(source, latKeys), 90);
    const lng = parseCoordinate(pickValue(source, lngKeys), 180);
    return lat !== null && lng !== null ? { lat, lng } : null;
};

const pickGeoJsonPair = (source) => {
    if (!source || typeof source !== 'object' || !Array.isArray(source.coordinates)) return null;
    const lng = parseCoordinate(source.coordinates[0], 180);
    const lat = parseCoordinate(source.coordinates[1], 90);
    return lat !== null && lng !== null ? { lat, lng } : null;
};

const nestedSources = (source, type) => {
    if (!source || typeof source !== 'object') return [];
    if (type === 'start') {
        return [source.startAddress, source.startLocation, source.startLoc, source.fromAddress, source.fromLocation];
    }
    if (type === 'end') {
        return [source.endAddress, source.endLocation, source.endLoc, source.toAddress, source.toLocation];
    }
    return [source.address, source.location];
};

export const getAddressText = (address, fallback = emptyText) => {
    if (!isPresent(address)) return fallback;
    if (typeof address === 'string') return address.trim() || fallback;
    if (typeof address !== 'object') return String(address);
    const label = [
        address.net_addr,
        address.fmtd_addr,
        address.address,
        address.location,
        address.near,
        address.locn,
        address.road,
        address.name,
    ].find((value) => isPresent(value) && typeof value !== 'object');
    return label || fallback;
};

const isBlankAddressLabel = (label) => {
    if (label === undefined || label === null) return true;
    if (typeof label !== 'string') return false;
    return ['', '-', '--', 'na', 'n/a', 'click for location'].includes(label.trim().toLowerCase());
};

export const resolveCoordinatePair = ({ lat, lng, source, address, type = 'direct' } = {}) => {
    const explicitPair = pickPair({ lat, lng }, DIRECT_LAT_KEYS, DIRECT_LNG_KEYS);
    if (explicitPair) return explicitPair;

    if (type === 'start') {
        const typedPair = pickPair(source, START_LAT_KEYS, START_LNG_KEYS);
        if (typedPair) return typedPair;
    }
    if (type === 'end') {
        const typedPair = pickPair(source, END_LAT_KEYS, END_LNG_KEYS);
        if (typedPair) return typedPair;
    }

    const candidateSources = [
        address,
        ...nestedSources(source, type),
        source,
    ].filter(Boolean);

    for (const candidate of candidateSources) {
        const pair = pickPair(candidate, DIRECT_LAT_KEYS, DIRECT_LNG_KEYS);
        if (pair) return pair;

        const geoJsonPair = pickGeoJsonPair(candidate) || pickGeoJsonPair(candidate?.location);
        if (geoJsonPair) return geoJsonPair;
    }

    return null;
};

export const getGoogleMapsUrl = (lat, lng) => {
    const pair = resolveCoordinatePair({ lat, lng });
    if (!pair) return '';
    return `https://www.google.com/maps/search/?api=1&query=${pair.lat},${pair.lng}`;
};

export const AddressMapLink = ({
    address,
    children,
    fallback = emptyText,
    lat,
    linkFallback = 'Click Here',
    lng,
    source,
    text,
    type = 'direct',
}) => {
    const rawLabel = children ?? text ?? getAddressText(address, '');
    const pair = resolveCoordinatePair({ lat, lng, source, address, type });
    const label = pair && isBlankAddressLabel(rawLabel)
        ? linkFallback
        : (isBlankAddressLabel(rawLabel) ? fallback : rawLabel);

    if (!pair) {
        return <>{label}</>;
    }

    return (
        <Link
            href={getGoogleMapsUrl(pair.lat, pair.lng)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            sx={{
                color: 'inherit',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
            }}
        >
            {label}
        </Link>
    );
};

export const renderAddressMapLink = (props) => <AddressMapLink {...props} />;
